import React, { useCallback, useMemo, useState } from 'react';
import { Autocomplete, BlockStack, Icon, InlineStack, Tag, Text } from '@shopify/polaris';
import { SearchIcon } from '@shopify/polaris-icons';

import { COUNTRY_OPTIONS, COUNTRY_NAME_BY_CODE } from '../utils/countries';

export default function CountryMultiSelect({ selected, onChange, disabled }) {
  const [inputValue, setInputValue] = useState('');

  const filteredOptions = useMemo(() => {
    if (!inputValue) return COUNTRY_OPTIONS;
    const q = inputValue.toLowerCase();
    return COUNTRY_OPTIONS.filter((o) => o.label.toLowerCase().includes(q));
  }, [inputValue]);

  const handleSelect = useCallback(
    (next) => {
      onChange(next);
      setInputValue('');
    },
    [onChange],
  );

  const removeCountry = useCallback(
    (code) => () => onChange(selected.filter((c) => c !== code)),
    [selected, onChange],
  );

  const textField = (
    <Autocomplete.TextField
      label="Target Countries"
      value={inputValue}
      onChange={setInputValue}
      prefix={<Icon source={SearchIcon} />}
      placeholder="Search countries"
      autoComplete="off"
      disabled={disabled}
      helpText="Pick one or more countries."
    />
  );

  return (
    <BlockStack gap="200">
      <Autocomplete
        allowMultiple
        options={filteredOptions}
        selected={selected}
        textField={textField}
        onSelect={handleSelect}
        listTitle="Countries"
      />
      {selected.length > 0 ? (
        <BlockStack gap="100">
          <Text variant="bodySm" tone="subdued">
            Selected ({selected.length}):
          </Text>
          <InlineStack gap="100" wrap>
            {selected.map((code) => (
              <Tag key={code} onRemove={disabled ? undefined : removeCountry(code)}>
                {COUNTRY_NAME_BY_CODE[code] ? `${COUNTRY_NAME_BY_CODE[code]} (${code})` : code}
              </Tag>
            ))}
          </InlineStack>
        </BlockStack>
      ) : (
        <Text variant="bodySm" tone="subdued">
          No countries selected - bar will show in all regions.
        </Text>
      )}
    </BlockStack>
  );
}
